import { lazy, Suspense, useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import AppShell from './components/AppShell'
import { SignInProvider } from './components/SignInModal'
import { EmailGateProvider } from './components/email/EmailGate'
import TranslatorPage from './routes/TranslatorPage'
import { importWithFreshAssetReload } from './lazy-route'

// Inlined from ./components/Onboarding so the lazy chunk boundary
// actually holds — importing `hasCompletedOnboarding` from that module
// would pull framer-motion + FocusLock into the main bundle.
const ONBOARDING_KEY = 'baba.app.onboarded'
function hasCompletedOnboarding(): boolean {
  try {
    if (typeof localStorage === 'undefined') return true
    return localStorage.getItem(ONBOARDING_KEY) === '1'
  } catch {
    return true
  }
}
import { getAuthEmail, isSignedIn } from '../lib/baba-auth'
import { identifyCrispUser } from '../lib/crisp'
import { useAppUpdateCheck } from '../lib/appUpdateCheck'
import { scrubAnalyticsUrl } from '../lib/analyticsUrl'

// Translate is the landing route and stays eager. Everything else splits off
// the main bundle, Chat included: it is one navigation away, so its chunk is
// fetched once the first page is idle (see App) rather than parsed on every
// first load.
const ChatPage = lazy(() => importWithFreshAssetReload(() => import('./routes/ChatPage')))
const LibraryPage = lazy(() => importWithFreshAssetReload(() => import('./routes/LibraryPage')))
const DictionaryPage = lazy(() => importWithFreshAssetReload(() => import('./routes/DictionaryPage')))
const DictionaryEntryPage = lazy(() => importWithFreshAssetReload(() => import('./routes/DictionaryEntryPage')))
const SettingsPage = lazy(() => importWithFreshAssetReload(() => import('./routes/SettingsPage')))
const ProfilePage = lazy(() => importWithFreshAssetReload(() => import('./routes/ProfilePage')))
const SituationsPage = lazy(() => importWithFreshAssetReload(() => import('./routes/SituationsPage')))
const SituationsReportPage = lazy(() => importWithFreshAssetReload(() => import('./routes/SituationsReportPage')))
const TutorPage = lazy(() => importWithFreshAssetReload(() => import('./routes/TutorPage')))
const WarmUpPage = lazy(() => importWithFreshAssetReload(() => import('./routes/WarmUpPage')))
const LearnPage = lazy(() => importWithFreshAssetReload(() => import('./routes/LearnPage')))
const LearnPlayPage = lazy(() => importWithFreshAssetReload(() => import('./routes/LearnPlayPage')))
const LearnRoundPage = lazy(() => importWithFreshAssetReload(() => import('./routes/LearnRoundPage')))
const ConversationPage = lazy(() => importWithFreshAssetReload(() => import('./routes/ConversationPage')))
const ShareViewPage = lazy(() => importWithFreshAssetReload(() => import('./routes/ShareViewPage')))
const ChatShareView = lazy(() => importWithFreshAssetReload(() => import('./routes/ChatShareView')))
const WhyTranslationWorksPage = lazy(() => importWithFreshAssetReload(() => import('./routes/WhyTranslationWorksPage')))
const UlpanWaitlistPage = lazy(() => importWithFreshAssetReload(() => import('./routes/UlpanWaitlistPage')))
const CheatSheetPage = lazy(() => importWithFreshAssetReload(() => import('./routes/CheatSheetPage')))
const LinkPage = lazy(() => importWithFreshAssetReload(() => import('./routes/LinkPage')))
const JoinPage = lazy(() => importWithFreshAssetReload(() => import('./routes/JoinPage')))
const OrgDashboardPage = lazy(() => importWithFreshAssetReload(() => import('./routes/OrgDashboardPage')))
// baba Email: gated per account (EmailGateProvider); the chunk holds every
// /email route and decides itself which of them exist.
const EmailRoutes = lazy(() => importWithFreshAssetReload(() => import('./routes/email/EmailRoutes')))
// Voice demo: reachable by URL only, deliberately absent from every nav.
const VoiceDemoPage = lazy(() => importWithFreshAssetReload(() => import('./routes/VoiceDemoPage')))
// Onboarding only runs for first-time visitors; defer it too.
const Onboarding = lazy(() => importWithFreshAssetReload(() => import('./components/Onboarding')))

/** Lightweight chunk-load fallback — never a blank screen on slow connections. */
function RouteFallback() {
  return (
    <div className="h-full min-h-[40vh] flex items-center justify-center" aria-busy="true">
      <span
        className="h-6 w-6 rounded-full border-2 animate-spin"
        style={{ borderColor: 'var(--border-strong)', borderTopColor: 'var(--accent)' }}
        aria-label="Loading"
      />
    </div>
  )
}

/**
 * The front door. "/" is Translate, for everyone, every time.
 *
 * Translate is the surface the product is built around and the thing a
 * visitor arriving cold should be shown. This used to send only first-run
 * browsers there and hand everyone else Chat, which meant the landing page
 * depended on a localStorage flag the visitor never set on purpose — a
 * cleared browser saw a different product than a returning one. Chat keeps
 * its own address at /chat/x and its place in the rail.
 *
 * Search and hash ride along: "/" is also where magic-link tokens, the
 * Google session fragment, referral codes, and the checkout return land, and
 * dropping them would break sign-in. They are read off the router's location
 * rather than window's, so a client-side navigation back to "/" carries the
 * same query it arrived with.
 */
function HomeRoute() {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: '/translate', search, hash }} replace />
}

/** The page as GA may see it: secret path segments templated, sign-in
 *  parameters removed (lib/analyticsUrl.ts). */
function sanitizedAnalyticsLocation(location: Location, routerLocation: { pathname: string; search: string }) {
  const pagePath = scrubAnalyticsUrl(routerLocation.pathname + routerLocation.search)
  return {
    pagePath,
    pageLocation: `${location.origin}${pagePath}`,
  }
}

function GAPageTracker() {
  const location = useLocation()
  useEffect(() => {
    // gtag() is defined synchronously in index.html's inline script, so
    // window.gtag is always a function by the time React mounts. But we also
    // push directly to dataLayer as a safety net — if gtag.js hasn't finished
    // loading yet, dataLayer still queues the event and gtag.js drains it on
    // attach. This guards against the "(not set)" landingPage issue on cold
    // loads where async gtag.js can race with React hydration.
    const w = window as unknown as {
      dataLayer?: unknown[]
      gtag?: (...args: unknown[]) => void
    }
    w.dataLayer = w.dataLayer || []
    const clean = sanitizedAnalyticsLocation(window.location, location)
    const payload = {
      page_path: clean.pagePath,
      page_location: clean.pageLocation,
      page_title: document.title,
    }
    // Every later event on this page (sign-up, checkout) reports the scrubbed
    // location too, instead of gtag's own read of the address bar.
    const pageDefaults = { page_path: clean.pagePath, page_location: clean.pageLocation }
    if (typeof w.gtag === 'function') {
      w.gtag('set', pageDefaults)
      w.gtag('event', 'page_view', payload)
    } else {
      w.dataLayer.push(['set', pageDefaults])
      w.dataLayer.push(['event', 'page_view', payload])
    }
  }, [location])
  return null
}

export default function App() {
  const [showOnboarding, setShowOnboarding] = useState(() => !hasCompletedOnboarding())
  const { pathname } = useLocation()
  useAppUpdateCheck()

  useEffect(() => {
    if (isSignedIn()) {
      const email = getAuthEmail()
      Sentry.setUser({ email: email ?? undefined })
      identifyCrispUser({ email })
    } else {
      Sentry.setUser(null)
    }
  }, [])

  // The checkout return (?checkout=success) is handled, purchase event
  // included, by AppShell before it strips the params.

  // Chat is one tap away from every page: fetch its chunk once the first
  // page has settled, so opening it does not wait on the network.
  useEffect(() => {
    const prefetch = () => void import('./routes/ChatPage').catch(() => {})
    const w = window as typeof window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
    if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(prefetch, { timeout: 5000 })
    else setTimeout(prefetch, 3000)
  }, [])

  return (
    <>
      <GAPageTracker />
      {/* The setup quiz is for baba users. An organization's dashboard, its
          join page and the phone handoff each have one job, and the quiz
          covered it. */}
      {showOnboarding && !/^\/(org|join|link)(\/|$)/.test(pathname) && (
        <Suspense fallback={null}>
          <Onboarding onComplete={() => setShowOnboarding(false)} />
        </Suspense>
      )}
      <EmailGateProvider>
      <Routes>
        {/* Share view stays shell-less — it's a public, logged-out surface. */}
        <Route
          path="chat/x/share/:token"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ChatShareView />
            </Suspense>
          }
        />
        {/*
         * Organization-sponsored access. Shell-less, and deliberately OUTSIDE
         * the app shell: a member arrives from a coordinator's message with one
         * thing to do. This is also the only place a sponsorship is ever
         * granted — the apps cannot do it (App Store 3.1.1), which is why the
         * whole flow lives on the web.
         */}
        <Route
          path="join/:slug"
          element={
            <Suspense fallback={<RouteFallback />}>
              {/* Shell-less pages get no SignInProvider from AppShell, and
                  useSignIn() silently no-ops without one. The join page has
                  to sign a member in, so it carries its own. */}
              <SignInProvider>
                <JoinPage />
              </SignInProvider>
            </Suspense>
          }
        />
        {/*
         * An organization's own dashboard, opened from the private link baba
         * sends its coordinator (app.itsbaba.com/org#k=...). Shell-less: it is a
         * tool for the organization, not part of the baba app.
         */}
        <Route
          path="org"
          element={
            <Suspense fallback={<RouteFallback />}>
              <OrgDashboardPage />
            </Suspense>
          }
        />
        {/*
         * Account-link QR target. Shell-less on purpose: a phone camera opens
         * it, and the only job is to hand the code to the app (or to the
         * store). Rail and tab bar would be noise on that trip.
         */}
        <Route
          path="link"
          element={
            <Suspense fallback={<RouteFallback />}>
              <LinkPage />
            </Suspense>
          }
        />
        <Route element={<AppShell />}>
          <Route path="/" element={<HomeRoute />} />
          <Route path="translate" element={<TranslatorPage />} />
          <Route
            path="chat/x"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ChatPage />
              </Suspense>
            }
          />
          <Route
            path="chat/x/:conversationId"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ChatPage />
              </Suspense>
            }
          />
          <Route path="chat/*" element={<Navigate to="/chat/x" replace />} />
          <Route
            path="library"
            element={
              <Suspense fallback={<RouteFallback />}>
                <LibraryPage />
              </Suspense>
            }
          />
          {/*
           * baba Email. Only rendered as a surface when GET /email/usage says
           * enabled; EmailRoutes waits for that answer and otherwise sends
           * the visitor home. The claim link inside it is reachable signed
           * out, because that is how a signed-out reader arrives.
           */}
          <Route
            path="email/*"
            element={
              <Suspense fallback={<RouteFallback />}>
                <EmailRoutes />
              </Suspense>
            }
          />
          <Route
            path="dictionary"
            element={
              <Suspense fallback={<RouteFallback />}>
                <DictionaryPage />
              </Suspense>
            }
          />
          <Route
            path="dictionary/:slug"
            element={
              <Suspense fallback={<RouteFallback />}>
                <DictionaryEntryPage />
              </Suspense>
            }
          />
          <Route
            path="situations"
            element={
              <Suspense fallback={<RouteFallback />}>
                <SituationsPage />
              </Suspense>
            }
          />
          <Route
            path="situations/:id"
            element={
              <Suspense fallback={<RouteFallback />}>
                <SituationsReportPage />
              </Suspense>
            }
          />
          <Route
            path="tutor"
            element={
              <Suspense fallback={<RouteFallback />}>
                <TutorPage />
              </Suspense>
            }
          />
          <Route
            path="warm-up"
            element={
              <Suspense fallback={<RouteFallback />}>
                <WarmUpPage />
              </Suspense>
            }
          />
          <Route
            path="learn"
            element={
              <Suspense fallback={<RouteFallback />}>
                <LearnPage />
              </Suspense>
            }
          />
          <Route
            path="learn/play"
            element={
              <Suspense fallback={<RouteFallback />}>
                <LearnPlayPage />
              </Suspense>
            }
          />
          <Route
            path="learn/round"
            element={
              <Suspense fallback={<RouteFallback />}>
                <LearnRoundPage />
              </Suspense>
            }
          />
          {/*
           * /voice-demo — the bilingual voice interaction demo. Not in the
           * rail, not in the tab bar: it is shown by sending the link.
           */}
          <Route
            path="voice-demo"
            element={
              <Suspense fallback={<RouteFallback />}>
                <VoiceDemoPage />
              </Suspense>
            }
          />
          <Route
            path="conversation"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ConversationPage />
              </Suspense>
            }
          />
          <Route
            path="why-translation-works"
            element={
              <Suspense fallback={<RouteFallback />}>
                <WhyTranslationWorksPage />
              </Suspense>
            }
          />
          <Route
            path="ulpan"
            element={
              <Suspense fallback={<RouteFallback />}>
                <UlpanWaitlistPage />
              </Suspense>
            }
          />
          <Route
            path="cheat-sheet"
            element={
              <Suspense fallback={<RouteFallback />}>
                <CheatSheetPage />
              </Suspense>
            }
          />
          {/* Splat: Settings handles its own section routes internally. */}
          <Route
            path="settings/*"
            element={
              <Suspense fallback={<RouteFallback />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="profile/*"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ProfilePage />
              </Suspense>
            }
          />
          {/* Account merged into Settings — keep old links working. */}
          <Route path="account" element={<Navigate to="/settings/account" replace />} />
          <Route
            path="t"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ShareViewPage />
              </Suspense>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </EmailGateProvider>
    </>
  )
}
